import React, { useEffect, useRef, useState } from 'react';
import Otto from './Otto';

// The Pinned page's "hello" — Otto flies in from the right, settles to the
// upper-right with a wave + a one-line speech bubble, then launches up-and-
// away and removes himself. He greets on EVERY page load (per request), so
// there is no once-per-session flag: a fresh mount (reload or re-navigation
// to /pinned) always replays the fly-through. Non-blocking: pointer-events
// off, never covers the cards, and he exits early if the user starts
// scrolling / clicking.
//
// prefers-reduced-motion: no flight — a simple fade in → hold → fade out of
// the greeting. Uses the shared #7 Flying / Waving symbols. Admin-only.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function OttoPinnedHello({ greeting, ready = true }) {
  const [phase, setPhase] = useState('hidden'); // hidden → enter → settle → exit → gone
  const [shown, setShown] = useState(false);
  const reducedRef = useRef(false);
  const timers = useRef([]);
  const startedRef = useRef(false);

  useEffect(() => {
    // Wait until the page has finished loading (ready), then greet — on
    // EVERY page load. The per-mount startedRef guard only prevents a
    // double fire within a single mount (StrictMode double-effect, or
    // `ready` toggling false→true); a fresh mount (reload / re-navigation
    // to /pinned) resets it, so Otto replays the fly-through each time.
    if (!ready || startedRef.current) return undefined;
    startedRef.current = true;

    const reduced = prefersReducedMotion();
    reducedRef.current = reduced;
    // Captured into a local so the cleanup below clears the SAME array this
    // effect filled. Reading timers.current at cleanup time would read
    // whatever the ref points at then, which is not guaranteed to be this.
    const pending = timers.current;
    const push = (fn, ms) => pending.push(setTimeout(fn, ms));

    setPhase('enter');
    // Fade-in flag (used by the reduced-motion path; harmless otherwise).
    push(() => setShown(true), 40);
    if (reduced) {
      push(() => setPhase('exit'), 2200);
      push(() => setPhase('gone'), 2650);
    } else {
      push(() => setPhase('settle'), 520);   // arrived → wave + bubble
      push(() => setPhase('exit'), 2300);     // launch away
      push(() => setPhase('gone'), 2960);     // removed
    }

    // Finish the exit early if the user starts interacting.
    const early = () => setPhase(p => (p === 'enter' || p === 'settle') ? 'exit' : p);
    window.addEventListener('scroll', early, { passive: true, once: true });
    window.addEventListener('pointerdown', early, { once: true });
    return () => {
      pending.forEach(clearTimeout);
      window.removeEventListener('scroll', early);
      window.removeEventListener('pointerdown', early);
    };
  }, [ready]);

  if (phase === 'hidden' || phase === 'gone') return null;

  const reduced = reducedRef.current;
  const showBubble = phase === 'settle' || (reduced && phase !== 'exit');
  const pose = (!reduced && (phase === 'enter' || phase === 'exit')) ? 'flying' : 'waving';

  // Transform / opacity per phase.
  let transform = 'translateX(130%)';
  let opacity = 1;
  let duration = '0.5s';
  if (reduced) {
    transform = 'none';
    opacity = (phase === 'exit' || !shown) ? 0 : 1;
    duration = '0.35s';
  } else if (phase === 'settle') {
    transform = 'translate(0,0)';
    duration = '0.5s';
  } else if (phase === 'exit') {
    transform = 'translate(45%,-165%) rotate(-14deg)';
    opacity = 0;
    duration = '0.6s';
  }

  return (
    <div
      aria-hidden="true"
      className="absolute z-20 pointer-events-none"
      style={{
        top: 8,
        // Clear of the header's "N pinned" count, which lives in the same
        // top-right corner (px-8 → ~32px inset, ~75px wide at "128 pinned").
        // Otto is 72px wide, so anchoring at right:40 landed his body straight
        // on top of it. Sitting him to the LEFT of the count keeps both
        // readable — the bubble then extends into the empty middle of the
        // header rather than over the title.
        right: 124,
        transform,
        opacity,
        transition: `transform ${duration} cubic-bezier(.3,.7,.3,1), opacity ${duration} ease`,
      }}>
      <div className="flex items-center gap-2">
        {showBubble && (
          <span className="whitespace-nowrap bg-white border border-line shadow-card rounded-2xl rounded-br-sm px-3.5 py-2 font-['Metropolis'] text-[13px] font-semibold text-ink-primary">
            {greeting}
          </span>
        )}
        <Otto pose={pose} size={72} ariaLabel="" />
      </div>
    </div>
  );
}
